import { useEffect } from "react";
import * as Sentry from "@sentry/react";

import type { User } from "@workos-inc/authkit-react";
import type { CachedIdentity } from "@/offline/db";

/**
 * Attaches the signed-in user to Sentry's GLOBAL scope, so every captured event
 * — including ones reported from non-React modules (offline sync, the outbox
 * drain) — names who hit it instead of only a rotating mobile IP.
 *
 * Identity precedence: the live WorkOS `user` when present, else the cached
 * offline identity. The fallback matters on the jobsite — `AuthGuard` lets a
 * previously-signed-in user into the app offline with no live `user`, and an
 * error captured then should still carry their identity. When neither exists
 * (signed out, or a fresh device) the Sentry user is cleared, so a sign-out
 * can't leave the previous user attached to the next person's events on a
 * shared device.
 *
 * Global scope (not the current scope) mirrors the existing `connectivity` tag
 * wiring in `@/offline/sentry-connectivity`, so identity applies uniformly to
 * events from every scope. Metadata only — id, email, display name — never
 * user-entered content. See `specs/conventions/error-reporting.md`.
 */
export function useSentryUser(
  user: User | null | undefined,
  cachedIdentity: CachedIdentity | null | undefined,
): void {
  const id = user?.id ?? cachedIdentity?.id ?? null;
  const email = user?.email ?? cachedIdentity?.email ?? null;
  const firstName = user?.firstName ?? cachedIdentity?.firstName ?? null;
  const lastName = user?.lastName ?? cachedIdentity?.lastName ?? null;

  useEffect(() => {
    const scope = Sentry.getGlobalScope();
    if (!id) {
      scope.setUser(null);
      return;
    }
    const username = [firstName, lastName].filter(Boolean).join(" ") || undefined;
    scope.setUser({ id, email: email ?? undefined, username });
  }, [id, email, firstName, lastName]);
}
